<template>
  <!-- 路面预瞄 HUD：全屏叠加在 3D 场景上，pointer-events-none 不拦截交互 -->
  <div class="absolute inset-0 pointer-events-none">
    <!-- 顶栏：主标题（与 vp 主题一致的霓虹渐变） -->
    <div
      class="absolute top-0 left-0 right-0 flex justify-center pt-5 pb-12 bg-gradient-to-b from-vp-background/95 via-vp-background/35 to-transparent"
    >
      <div class="relative px-12 py-4">
        <div
          class="pointer-events-none absolute inset-0 rounded-2xl border border-vp-accent/30 bg-vp-secondary/40 backdrop-blur-md shadow-[0_0_48px_rgba(0,212,255,0.12)]"
        />
        <div
          class="pointer-events-none absolute -inset-px rounded-2xl bg-gradient-to-r from-vp-accent/0 via-vp-accent/20 to-vp-accent/0 opacity-70"
        />
        <h1
          class="relative text-center text-3xl sm:text-4xl font-black tracking-[0.35em] text-transparent bg-clip-text bg-gradient-to-r from-slate-100 via-cyan-200 to-vp-accent drop-shadow-[0_0_18px_rgba(0,212,255,0.35)]"
        >
          路面预瞄系统
        </h1>
        <div
          class="pointer-events-none absolute left-1/2 bottom-2 h-[3px] w-[72%] -translate-x-1/2 rounded-full bg-gradient-to-r from-transparent via-vp-accent to-transparent opacity-90 shadow-[0_0_12px_rgba(0,212,255,0.6)]"
        />
      </div>
    </div>

    <!-- 近距离障碍物预警（目标物逼近，距离 ≤15m）— 已整体注释关闭
    <div
      v-if="isWarning && nearestObstacle"
      class="absolute top-20 left-1/2 -translate-x-1/2 animate-pulse"
    >
      <div
        class="bg-red-500/90 border-2 border-red-400 rounded-lg px-6 py-3 flex items-center gap-3"
      >
        <svg
          class="w-6 h-6 text-white shrink-0"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"
          />
          <path d="M12 9v4" />
          <path d="M12 17h.01" />
        </svg>
        <div class="text-white">
          <div class="font-bold text-lg">
            {{
              nearestObstacle.type === "speedBump" ? "前方减速带" : "前方井盖"
            }}
          </div>
          <div class="text-sm font-mono">
            距离: {{ nearestObstacle.distance }}m - 请减速
          </div>
        </div>
      </div>
    </div>
    -->

    <!-- 左侧面板：与右侧同 top，左右对称 -->
    <div class="absolute left-4 top-36 flex flex-col gap-3">
      <!-- 车速 -->
      <div
        class="bg-vp-secondary/80 border border-vp-border rounded-lg p-3 w-44"
      >
        <div class="flex items-center gap-2 mb-2">
          <svg
            class="w-4 h-4 text-vp-primary shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m12 14 4-4" />
            <path d="M3.34 19a10 10 0 1 1 17.32 0" />
          </svg>
          <span class="text-xs font-mono text-vp-muted-foreground">速度</span>
        </div>
        <div class="flex items-baseline gap-1">
          <span class="text-3xl font-bold font-mono text-vp-primary">{{
            speedDisplay
          }}</span>
          <span class="text-sm text-vp-muted-foreground">km/h</span>
        </div>
      </div>
      <!-- 检测目标数 -->
      <div
        class="bg-vp-secondary/80 border border-vp-border rounded-lg p-3 w-44"
      >
        <div class="flex items-center gap-2 mb-2">
          <svg
            class="w-4 h-4 text-vp-accent shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"
            />
            <circle cx="12" cy="12" r="3" />
          </svg>
          <span class="text-xs font-mono text-vp-muted-foreground"
            >检测目标</span
          >
        </div>
        <div class="flex items-baseline gap-1">
          <span class="text-3xl font-bold font-mono text-vp-accent">{{
            obstacles.length
          }}</span>
          <span class="text-sm text-vp-muted-foreground">个</span>
        </div>
      </div>
      <!-- 垂向加速度 SusIMUAccVert（设置中开关，默认关） -->
      <div
        v-if="showSusImuAccVertChart"
        class="bg-vp-secondary/80 border border-vp-border rounded-lg p-3 w-44 shrink-0"
      >
        <div class="flex items-center gap-2 mb-2">
          <svg
            class="w-4 h-4 text-cyan-400 shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M3 3v18h18" />
            <path d="m19 9-5 5-4-4-3 3" />
          </svg>
          <span class="text-xs font-mono text-vp-muted-foreground"
            >垂向加速度</span
          >
        </div>
        <div class="h-[88px] w-full rounded bg-vp-background/40 overflow-hidden">
          <v-chart
            class="!h-[88px] !w-full"
            :option="susImuChartOption"
            autoresize
          />
        </div>
      </div>
      <!-- 传感器在线状态列表：暂无 CarProperty 信号映射，整段注释保留便于恢复
      <div
        class="bg-vp-secondary/80 border border-vp-border rounded-lg p-3 w-44"
      >
        <div class="flex items-center gap-2 mb-2">
          <svg
            class="w-4 h-4 text-emerald-400 shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9" />
            <path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5" />
            <circle cx="12" cy="12" r="2" />
            <path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5" />
            <path d="M19.1 4.9C23 8.8 23 15.1 19.1 19" />
          </svg>
          <span class="text-xs font-mono text-vp-muted-foreground">传感器</span>
        </div>
        <div class="space-y-1.5 mt-2">
          <div
            v-for="row in sensorRows"
            :key="row.label"
            class="flex items-center justify-between text-xs"
          >
            <span class="font-mono text-vp-muted-foreground">{{
              row.label
            }}</span>
            <div class="flex items-center gap-1">
              <div
                class="w-1.5 h-1.5 rounded-full"
                :class="
                  row.status === 'active' ? 'bg-emerald-400' : 'bg-red-500'
                "
              />
              <span
                :class="
                  row.status === 'active' ? 'text-emerald-400' : 'text-red-500'
                "
              >
                {{ row.status === "active" ? "在线" : "离线" }}
              </span>
            </div>
          </div>
        </div>
      </div>
      -->
    </div>

    <!-- 右侧面板：与左侧同 top；固定 top 避免列表高度导致跳动 -->
    <div class="absolute right-4 top-36 flex w-56 flex-col gap-3">
      <!-- 雷达地图（Canvas 由脚本绘制扫描与目标点） -->
      <div
        class="bg-vp-secondary/80 border border-vp-border rounded-lg p-3 w-full shrink-0"
      >
        <div class="flex items-center gap-2 mb-3">
          <div
            class="w-4 h-4 rounded-full border-2 border-vp-accent flex items-center justify-center"
          >
            <div class="w-1.5 h-1.5 rounded-full bg-vp-accent" />
          </div>
          <span class="text-xs font-mono text-vp-muted-foreground">雷达地图</span>
          <span class="text-xs font-mono text-vp-accent ml-auto"
            >{{ PREVIEW_SCENE_HUD_RADAR_RANGE_M }}m</span
          >
        </div>
        <canvas
          ref="radarCanvasRef"
          width="200"
          height="200"
          class="w-full h-auto rounded bg-vp-background/50"
        />
      </div>
      <!-- 障碍物列表：固定高度（约两条），条数变化不撑缩外框 -->
      <div
        class="bg-vp-secondary/80 border border-vp-border rounded-lg p-3 w-full shrink-0"
      >
        <div class="flex items-center gap-2 mb-3">
          <svg
            class="w-4 h-4 text-amber-400 shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"
            />
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
          </svg>
          <span class="text-xs font-mono text-vp-muted-foreground"
            >障碍物列表</span
          >
        </div>
        <div class="h-20 overflow-y-auto">
          <template v-if="obstacles.length === 0">
            <div
              class="h-full flex items-center justify-center text-xs text-vp-muted-foreground px-1 text-center"
            >
              前方无障碍物
            </div>
          </template>
          <template v-else>
            <div class="space-y-2">
              <div
                v-for="obs in obstacles"
                :key="obs.id"
                class="flex items-center justify-between text-sm p-2 rounded"
                :class="obstacleRowClass(obs)"
              >
                <div class="flex items-center gap-2">
                  <div
                    class="w-2 h-2 rounded-full"
                    :class="
                      obs.type === 'speedBump' ? 'bg-amber-400' : 'bg-cyan-400'
                    "
                  />
                  <span class="text-vp-foreground font-medium">{{
                    obs.type === "speedBump" ? "减速带" : "井盖"
                  }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <span
                    class="font-mono font-bold"
                    :class="obstacleDistClass(obs)"
                  >
                    {{ obs.distance }}m
                  </span>
                  <!-- 近距离目标物警示图标 — 已注释关闭
                  <svg
                    v-if="obs.distance <= WARNING_DISTANCE_THRESHOLD_M"
                    class="w-4 h-4 text-red-400 shrink-0"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path
                      d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"
                    />
                    <path d="M12 9v4" />
                    <path d="M12 17h.01" />
                  </svg>
                  -->
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- 底栏：经纬度与 GPS 摘要（演示用固定卫星数） -->
    <div
      class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-vp-background/80 to-transparent p-3"
    >
      <div
        class="flex justify-center items-center gap-6 text-xs font-mono text-vp-muted-foreground"
      >
        <span>LAT: {{ latDisplay }}</span>
        <span class="text-vp-border">|</span>
        <span>LON: {{ lngDisplay }}</span>
        <span class="text-vp-border">|</span>
        <span class="text-emerald-400">GPS: 12 SAT</span>
      </div>
    </div>

    <!-- 四角 HUD 装饰线 -->
    <!-- <div
      v-for="corner in cornerDecorations"
      :key="corner"
      class="absolute text-vp-accent/40"
      :class="cornerPositionClass(corner)"
    >
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
        <path
          d="M0 32V0H32"
          stroke="currentColor"
          stroke-width="1"
        />
      </svg>
    </div> -->
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from "vue";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart } from "echarts/charts";
import { GridComponent } from "echarts/components";
import type { EChartsOption } from "echarts";
import VChart from "vue-echarts";
import type { ObstacleInfo } from "./vehiclePerceptionTypes";
import { PREVIEW_SCENE_HUD_RADAR_RANGE_M } from "@/constants/previewSceneDisplay";

use([CanvasRenderer, LineChart, GridComponent]);

/** 障碍物列表、车速、经纬度（雷达量程见 `PREVIEW_SCENE_HUD_RADAR_RANGE_M`） */
const props = withDefaults(
  defineProps<{
    obstacles: ObstacleInfo[];
    vehicleSpeedKmh?: number;
    latitude?: number;
    longitude?: number;
    /** 设置面板：是否显示 SusIMUAccVert 曲线（默认关） */
    showSusImuAccVertChart?: boolean;
    /** 垂向加速度历史采样（时间序） */
    susImuAccVertSamples?: number[];
  }>(),
  {
    vehicleSpeedKmh: 0,
    latitude: 0,
    longitude: 0,
    showSusImuAccVertChart: false,
    susImuAccVertSamples: () => [],
  }
);

const susImuChartOption = computed<EChartsOption>(() => {
  const raw = props.susImuAccVertSamples ?? [];
  const values = raw.length > 0 ? raw : [0];
  const xData = values.map((_, i) => i);
  return {
    animation: false,
    backgroundColor: "transparent",
    grid: { left: 0, right: 2, top: 2, bottom: 0, containLabel: false },
    xAxis: {
      type: "category",
      show: false,
      boundaryGap: false,
      data: xData,
    },
    yAxis: {
      type: "value",
      show: false,
      min: -2,
      max: 2,
    },
    series: [
      {
        type: "line",
        data: values,
        smooth: 0.35,
        symbol: "none",
        lineStyle: {
          color: "rgba(34, 211, 238, 0.95)",
          width: 2,
          shadowBlur: 6,
          shadowColor: "rgba(0, 212, 255, 0.45)",
        },
        areaStyle: {
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: "rgba(34, 211, 238, 0.22)" },
              { offset: 1, color: "rgba(34, 211, 238, 0.02)" },
            ],
          },
        },
      },
    ],
  };
});

/** 雷达扫描角度、雷达 canvas 引用 */
const radarAngle = ref(0);
const radarCanvasRef = ref<HTMLCanvasElement | null>(null);

/** 左栏传感器：原静态 LIDAR/CAMERA/RADAR；待定信号后恢复模板注释并启用 */
// const sensorRows = [
//   { label: "LIDAR", status: "active" as const },
//   { label: "CAMERA", status: "active" as const },
//   { label: "RADAR", status: "active" as const },
// ];

// 计算属性：车速 / 经纬度格式化
const speedDisplay = computed(() =>
  Math.round(Math.max(0, props.vehicleSpeedKmh ?? 0))
);

const latDisplay = computed(() => {
  const lat = props.latitude ?? 0;
  const suffix = lat >= 0 ? "N" : "S";
  return `${Math.abs(lat).toFixed(5)} ${suffix}`;
});

const lngDisplay = computed(() => {
  const lng = props.longitude ?? 0;
  const suffix = lng >= 0 ? "E" : "W";
  return `${Math.abs(lng).toFixed(5)} ${suffix}`;
});

/** 列表次警示距离上限（与 HUD 雷达量程挂钩，封顶 60m） */
const cautionDistanceThresholdM = computed(() =>
  Math.min(60, PREVIEW_SCENE_HUD_RADAR_RANGE_M * 2)
);

/* 目标物逼近预警（最近障碍 ≤15m 顶栏红条）— 已关闭；原阈值 WARNING_DISTANCE_THRESHOLD_M = 15
const nearestObstacle = computed(() => props.obstacles[0]);
const isWarning = computed(() => {
  const near = nearestObstacle.value;
  if (!near) {
    return false;
  }
  return near.distance <= 15;
});
*/

function obstacleRowClass (obs: ObstacleInfo): string {
  if (obs.distance < cautionDistanceThresholdM.value) {
    return "bg-amber-500/10 border border-amber-500/30";
  }
  return "bg-vp-secondary/50";
}

function obstacleDistClass (obs: ObstacleInfo): string {
  if (obs.distance < cautionDistanceThresholdM.value) {
    return "text-amber-400";
  }
  return "text-vp-muted-foreground";
}

// 右侧雷达图：距离环、扫描扇面、本车朝向、障碍物在雷达平面上的绘制（rangeM 为量程米）
function drawRadarMap (
  canvas: HTMLCanvasElement,
  angle: number,
  obstacles: ObstacleInfo[],
  rangeM: number
): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return;
  }
  const size = canvas.width;
  const center = size / 2;
  const radius = size / 2 - 8;

  ctx.clearRect(0, 0, size, size);

  ctx.strokeStyle = "rgba(0, 212, 255, 0.3)";
  ctx.lineWidth = 1;
  for (let i = 1; i <= 4; i++) {
    ctx.beginPath();
    ctx.arc(center, center, (radius / 4) * i, 0, Math.PI * 2);
    ctx.stroke();
  }

  ctx.fillStyle = "rgba(0, 212, 255, 0.5)";
  ctx.font = "9px monospace";
  const range = Math.max(1, rangeM);
  for (let i = 1; i <= 3; i++) {
    const labelM = Math.round((range / 4) * i);
    ctx.fillText(
      `${labelM}m`,
      center + 2,
      center - (radius / 4) * i + 3
    );
  }

  ctx.strokeStyle = "rgba(0, 212, 255, 0.2)";
  ctx.beginPath();
  ctx.moveTo(center, 8);
  ctx.lineTo(center, size - 8);
  ctx.moveTo(8, center);
  ctx.lineTo(size - 8, center);
  ctx.stroke();

  const sweepAngle = (angle * Math.PI) / 180;
  const gradient = ctx.createConicGradient(sweepAngle, center, center);
  gradient.addColorStop(0, "rgba(0, 212, 255, 0.3)");
  gradient.addColorStop(0.08, "rgba(0, 212, 255, 0.1)");
  gradient.addColorStop(0.12, "rgba(0, 212, 255, 0)");
  gradient.addColorStop(1, "rgba(0, 212, 255, 0)");

  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(center, center, radius, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = "rgba(0, 212, 255, 0.8)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(center, center);
  ctx.lineTo(
    center + Math.cos(sweepAngle - Math.PI / 2) * radius,
    center + Math.sin(sweepAngle - Math.PI / 2) * radius
  );
  ctx.stroke();

  ctx.fillStyle = "#22d3ee";
  ctx.beginPath();
  ctx.moveTo(center, center - 10);
  ctx.lineTo(center - 6, center + 6);
  ctx.lineTo(center + 6, center + 6);
  ctx.closePath();
  ctx.fill();

  const scale = radius / range;
  obstacles.forEach((obs) => {
    let radarX = center + obs.x * scale * 2;
    let radarY = center + obs.z * scale;
    const dx = radarX - center;
    const dy = radarY - center;
    const distFromCenter = Math.hypot(dx, dy);
    if (distFromCenter > radius && distFromCenter > 1e-6) {
      const t = radius / distFromCenter;
      radarX = center + dx * t;
      radarY = center + dy * t;
    }
    if (obs.type === "speedBump") {
      ctx.fillStyle = "#fbbf24";
      ctx.fillRect(radarX - 10, radarY - 3, 20, 6);
      ctx.strokeStyle = "#fbbf24";
      ctx.lineWidth = 1;
      ctx.strokeRect(radarX - 12, radarY - 5, 24, 10);
    } else {
      ctx.fillStyle = "#22d3ee";
      ctx.beginPath();
      ctx.arc(radarX, radarY, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#22d3ee";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(radarX, radarY, 8, 0, Math.PI * 2);
      ctx.stroke();
    }
  });
}

// 扫描角或障碍物数据变化时同步重绘 canvas
watch(
  [radarAngle, () => props.obstacles],
  () => {
    const c = radarCanvasRef.value;
    if (c) {
      drawRadarMap(
        c,
        radarAngle.value,
        props.obstacles,
        PREVIEW_SCENE_HUD_RADAR_RANGE_M
      );
    }
  },
  { deep: true }
);

let radarIntervalId: ReturnType<typeof setInterval> | null = null;

// 雷达扫描角定时递增；首帧 nextTick 后绘制一次
onMounted(() => {
  radarIntervalId = setInterval(() => {
    radarAngle.value = (radarAngle.value + 3) % 360;
  }, 30);
  void nextTick(() => {
    const c = radarCanvasRef.value;
    if (c) {
      drawRadarMap(
        c,
        radarAngle.value,
        props.obstacles,
        PREVIEW_SCENE_HUD_RADAR_RANGE_M
      );
    }
  });
});

onUnmounted(() => {
  if (radarIntervalId) {
    clearInterval(radarIntervalId);
  }
});
</script>
